import { useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'
import { AppRoot } from '../components/AppRoot'
import { signOut } from '../lib/auth'
import { Button } from '../ui'

/**
 * Shown at `/` when `decideGate` returns `'PENDING'` — signed in, profile exists, but the
 * account's invite hasn't been redeemed (or redemption didn't activate it; `'DISABLED'` is the
 * separate, revoked-account case). Links to `/join`, which is reachable from this state so a
 * PENDING user can redeem a code without signing out first.
 *
 * Wrapped in exactly one `<AppRoot>`, like every other top-level screen (`JoinScreen`'s
 * not-signed-in branch explains why exactly one, never zero, never two). This is the screen every
 * invited paramedic sees first, so it is the one that can least afford to render outside
 * `.sl-root` — where `base.css` never sets `--ink` and a dark OS preference leaves black text on
 * the near-black `--paper` that `app.css` paints onto `body`.
 */
export function PendingNotice(): ReactElement {
  const [signingOut, setSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  async function handleSignOut() {
    setSigningOut(true)
    setSignOutError(null)
    const { error } = await signOut()
    // No navigation needed on success — SessionProvider's onAuthStateChange subscription picks
    // up the cleared session and AppRoutes redirects to /sign-in on its own next render.
    if (error) {
      setSignOutError(error.message)
      setSigningOut(false)
    }
  }

  return (
    <AppRoot>
      <div className="sl-stack">
        <div>
          <h1 className="sl-heading">You need an invite code.</h1>
          <p className="sl-hint">
            You&apos;re signed in, but you&apos;ll need an invite code to get in — ask whoever
            invited you for one.
          </p>
        </div>
        <Link to="/join">Enter invite code</Link>
        <Button variant="secondary" block disabled={signingOut} onClick={() => void handleSignOut()}>
          Sign out
        </Button>
        {signOutError ? (
          <p className="sl-hint" role="alert">
            {signOutError}
          </p>
        ) : null}
      </div>
    </AppRoot>
  )
}
